/**
 * Account → Sessions & security (W1). Lists the user's active sessions (devices) and lets them
 * revoke a single device or sign out everywhere. Backed by the W0 session layer:
 *   GET /api/auth/sessions ; DELETE /api/auth/sessions/:id ; POST /api/auth/logout-all
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { useState } from 'react';
import { ActivityIndicator, Alert, StyleSheet, View } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { listSessions, revokeSession, logoutAll } from '../../../../src/api/account';
import { useSession } from '../../../../src/auth/session';
import { isWebPushSupported, enableWebPush, notificationPermission } from '../../../../src/push/registerWebPush';
import { Screen, Text, Card, Button, ListRow } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage } from '../../../../src/lib/format';
import type { SessionDto } from '@beam/api-types';

function clientLabel(c: SessionDto['client']): string {
  return c === 'web' ? 'Web browser' : c === 'ios' ? 'iPhone / iPad' : 'Android';
}
function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export default function SecurityScreen() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const localLogout = useSession((s) => s.logout);

  const sessionsQuery = useQuery({ queryKey: ['sessions'], queryFn: listSessions, staleTime: 15_000 });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeSession(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
    // REM-MOB-02 — surface failures. A security action must NOT silently no-op: if revoke fails the
    // user has to know the device is still trusted so they can retry.
    onError: () => Alert.alert('Could not sign out that device', 'Please check your connection and try again.'),
  });

  const logoutAllMutation = useMutation({
    mutationFn: () => logoutAll(),
    onSuccess: async () => {
      // logout-all revokes every session (including this one) — clear local state + route to login.
      await localLogout();
    },
    // REM-MOB-02 — surface failures instead of a silent no-op.
    onError: () => Alert.alert('Could not sign out everywhere', 'Please check your connection and try again.'),
  });

  const sessions = sessionsQuery.data ?? [];

  // Web-only: enable browser push notifications on this device.
  const pushSupported = isWebPushSupported();
  const [pushStatus, setPushStatus] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState(false);

  async function handleEnablePush() {
    setPushBusy(true);
    try {
      const result = await enableWebPush();
      setPushStatus(
        result === 'enabled'
          ? 'Notifications enabled on this device.'
          : result === 'denied'
            ? 'Permission denied — enable notifications in your browser settings.'
            : result === 'unavailable'
              ? 'Push is not configured on the server yet.'
              : 'Not supported in this browser.',
      );
    } catch {
      setPushStatus('Could not enable notifications.');
    } finally {
      setPushBusy(false);
    }
  }

  return (
    <Screen scroll>
      <Text variant="body" color="secondary">
        These are the devices currently signed in to your account. Revoke any you don't recognize.
      </Text>

      {sessionsQuery.isLoading ? (
        <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading sessions" />
      ) : sessionsQuery.isError ? (
        <Card>
          <Text variant="body" color="danger">{extractErrorMessage(sessionsQuery.error, 'Failed to load sessions.')}</Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => sessionsQuery.refetch()} />
        </Card>
      ) : sessions.length === 0 ? (
        <Text variant="body" color="secondary">No other active sessions.</Text>
      ) : (
        <View style={styles.list}>
          {sessions.map((s) => (
            <ListRow
              key={s.id}
              title={clientLabel(s.client)}
              subtitle={`Last used ${when(s.last_used_at)}`}
              right={
                <Button
                  title="Revoke"
                  variant="secondary"
                  size="sm"
                  fullWidth={false}
                  loading={revokeMutation.isPending && revokeMutation.variables === s.id}
                  onPress={() => revokeMutation.mutate(s.id)}
                  accessibilityLabel={`Revoke ${clientLabel(s.client)} session`}
                />
              }
            />
          ))}
        </View>
      )}

      {pushSupported ? (
        <Card>
          <Text variant="bodyStrong">Notifications on this device</Text>
          <Text variant="small" color="secondary">
            Get a browser notification when work is assigned to you — even when this tab is in the background.
          </Text>
          <Button
            title={notificationPermission() === 'granted' ? 'Re-register notifications' : 'Enable notifications'}
            variant="secondary"
            onPress={handleEnablePush}
            loading={pushBusy}
            accessibilityLabel="Enable browser notifications"
          />
          {pushStatus ? <Text variant="small" color="secondary">{pushStatus}</Text> : null}
        </Card>
      ) : null}

      <Card>
        <Text variant="bodyStrong">Change password</Text>
        <Text variant="small" color="secondary">
          Update your password. This signs you out of every other device.
        </Text>
        <Button
          title="Change password"
          variant="secondary"
          onPress={() => router.push('/(app)/account/change-password')}
          accessibilityLabel="Change password"
        />
      </Card>

      <Card>
        <Text variant="bodyStrong">Sign out everywhere</Text>
        <Text variant="small" color="secondary">
          Ends every session on every device, including this one. Use this if a device was lost or stolen.
        </Text>
        <Button
          title="Sign out of all devices"
          variant="secondary"
          onPress={() => logoutAllMutation.mutate()}
          loading={logoutAllMutation.isPending}
          accessibilityLabel="Sign out of all devices"
        />
      </Card>

      <Card>
        <Text variant="bodyStrong" color="danger">Delete account</Text>
        <Text variant="small" color="secondary">
          Permanently remove your organization and all of its data. This cannot be undone.
        </Text>
        <Button
          title="Delete account"
          variant="danger"
          onPress={() => router.push('/(app)/account/delete-account')}
          accessibilityLabel="Delete account"
        />
      </Card>
    </Screen>
  );
}

const styles = StyleSheet.create({
  list: { gap: theme.spacing.sm },
});
